'use client';

import { VariableBlinqLogo } from '@components/BlinqAssets';
import { faCheck, faCopy, faLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { analytics } from '@modules/analytics';
import { copyToClipboard } from '@modules/helpers';
import { Box, Inline, Overlay, Stack, Text } from '@ui-lib';
import { useState } from 'react';

import { overlayStyleOverrides, overlayStyleOverridesHome } from '../shared.css';
import {
  checkMarkStyle,
  containerStyle,
  dashboardLinkButtonStyle,
  fadeDownAnimationStyle,
  headingSizeOverride,
  imageStyle,
  logoStyle,
} from './styles.css';

const DASHBOARD_LINK = 'https://dash.blinq.me/';

const DashboardLinkButton = ({ isFromOnboarding }: { isFromOnboarding: boolean }) => {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async () => {
    if (isFromOnboarding) {
      analytics.track({
        name: 'Onboarding_Redirect_Link_Copied',
      });
    }
    try {
      await copyToClipboard(DASHBOARD_LINK);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      component="button"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      paddingY="medium"
      paddingX="medium"
      boxShadow="borderNeutral"
      borderRadius="large"
      background={copied ? 'positiveLight' : 'body'}
      className={dashboardLinkButtonStyle}
      onClick={handleCopy}
    >
      <Inline alignY="center" nowrap gap="small">
        <FontAwesomeIcon size="lg" icon={faLink} />
        <Text>{DASHBOARD_LINK}</Text>
      </Inline>
      {copied ? (
        <Inline alignY="center" gap="xsmall">
          <FontAwesomeIcon icon={faCheck} className={copied ? checkMarkStyle : undefined} />
          <Text tone="positive" size="small">
            Copied!
          </Text>
        </Inline>
      ) : (
        <FontAwesomeIcon icon={faCopy} size="lg" />
      )}
    </Box>
  );
};

export const TeamAdminUnsupportedBreakpointOverlay = ({
  useLegacyHomeBreakpoint = false,
  isFromOnboarding,
}: {
  useLegacyHomeBreakpoint?: boolean;
  isFromOnboarding: boolean;
}) => {
  const title = isFromOnboarding ? 'You’re all set up!' : 'Continue via desktop browser';
  const message = isFromOnboarding
    ? 'Unlock full access via desktop browser'
    : 'The admin dashboard is only available on desktop';

  return (
    <Overlay
      className={useLegacyHomeBreakpoint ? overlayStyleOverridesHome : overlayStyleOverrides}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        paddingX="medium"
        className={containerStyle}
      >
        <Stack gap="large" align="center">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={{
              mobile: 'medium',
              tablet: 'large',
            }}
            className={fadeDownAnimationStyle}
          >
            <VariableBlinqLogo iconOnly className={logoStyle} />
            <Box component="h1" textAlign="center" className={headingSizeOverride}>
              {title}
            </Box>
            <Text tone="secondary" align="center">
              {message}
            </Text>
          </Box>
          <DashboardLinkButton isFromOnboarding={isFromOnboarding} />
          <Box component="img" src="/img/mbp.webp" className={imageStyle} />
        </Stack>
      </Box>
    </Overlay>
  );
};
